import React, { useEffect, useState, useMemo } from "react";
import { useCollection, api, MONEY_STATUSES, moneyStatusColor, fmtMoney, fmtMoneyShort, fmtDate } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, MetricCard, Labeled } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit, Trash2, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";

const empty = { party: "", opportunity: "", amount: 0, status: "promised", last_contact: "", next_action: "", expected_date: "", owner: "" };

export default function Money() {
  const { items, loading, create, update, remove } = useCollection("money");
  const [summary, setSummary] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const loadSummary = () => api.get("/money/summary").then(({ data }) => setSummary(data)).catch(() => {});
  useEffect(() => { loadSummary(); }, [items]);

  const openNew = () => { setForm(empty); setEditId(null); setDlg(true); };
  const openEdit = (m) => { setForm({ ...empty, ...m }); setEditId(m.id); setDlg(true); };
  const save = async () => {
    if (!form.party.trim()) { toast.error("Who owes / pays?"); return; }
    const payload = { ...form, amount: Number(form.amount) || 0 };
    if (editId) await update(editId, payload); else await create(payload);
    toast.success("Saved"); setDlg(false); loadSummary();
  };
  const setStatus = async (m, status) => { const extra = status === "paid" ? { paid_date: new Date().toISOString() } : {}; await update(m.id, { status, ...extra }); toast.success(status === "paid" ? "Marked paid" : `→ ${status}`); loadSummary(); };

  if (loading || !summary) return <Loading label="Counting the money…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Money" subtitle="Executive revenue command — not accounting" testId="money-header"
        action={<button data-testid="money-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><Plus size={16} /> Add Money</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Collected Today" value={fmtMoneyShort(summary.collected_today)} accent="#10B981" testId="money-today" />
        <MetricCard label="Collected Week" value={fmtMoneyShort(summary.collected_week)} accent="#10B981" testId="money-week" />
        <MetricCard label="Collected Month" value={fmtMoneyShort(summary.collected_month)} accent="#10B981" testId="money-month" />
        <MetricCard label="Expected" value={fmtMoneyShort(summary.expected)} testId="money-expected" />
        <MetricCard label="Overdue" value={fmtMoneyShort(summary.overdue)} accent={summary.overdue > 0 ? "#E11D48" : "#FAFAFA"} testId="money-overdue" />
        <MetricCard label="Sales Pipeline" value={fmtMoneyShort(summary.pipeline)} testId="money-pipeline" />
        <MetricCard label="Closeable This Week" value={fmtMoneyShort(summary.closeable)} accent="#F59E0B" testId="money-closeable-card" />
      </div>

      <div>
        <div className="label-caps mb-3">Money on the Table</div>
        {items.length === 0 ? <EmptyState testId="money-empty" title="No money has been entered yet." hint="Track every dollar promised, pending, and overdue." action={<button onClick={openNew} className="px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm">+ Add Money</button>} /> : (
          <div className="space-y-3">
            {items.map((m) => (
              <div key={m.id} data-testid={`money-${m.id}`} className="bg-surface border border-edge rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-semibold truncate">{m.party}</span><span className="font-num text-lg" style={{ color: moneyStatusColor(m.status) }}>{fmtMoney(m.amount)}</span></div>
                    {m.opportunity && <p className="text-sm text-[#A3A3A3] truncate">{m.opportunity}</p>}
                    {m.next_action && <p className="text-sm mt-1"><span className="label-caps">Next: </span>{m.next_action}</p>}
                    {m.expected_date && <p className="text-xs text-samber mt-1 font-num">Expected {fmtDate(m.expected_date)}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0"><button onClick={() => openEdit(m)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={14} /></button><button onClick={async () => { await remove(m.id); loadSummary(); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={14} /></button></div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {MONEY_STATUSES.map((s) => (
                    <button key={s} data-testid={`money-status-${m.id}-${s}`} onClick={() => setStatus(m, s)}
                      className={cn("px-2.5 py-1 rounded-full text-[0.65rem] font-bold border transition-colors", m.status === s ? "text-ink border-transparent" : "border-edge text-[#A3A3A3] hover:text-[#FAFAFA]")}
                      style={m.status === s ? { backgroundColor: moneyStatusColor(s) } : {}}>{s.replace("_", " ")}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit" : "Add"} Money Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Person / Organization"><Input data-testid="money-party" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Amount ($)"><Input data-testid="money-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{MONEY_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></Labeled>
              <Labeled label="Expected Date"><Input data-testid="money-expected-date" type="date" value={(form.expected_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Owner"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="Opportunity"><Input value={form.opportunity} onChange={(e) => setForm({ ...form, opportunity: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Next Action"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="money-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
